import moment from 'moment';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input } from 'semantic-ui-react';
import ObligationsDropdown from 'src/containers/ObligationsDropdown/ObligationsDropdown';
import { GetPossibleMatchDocuments } from 'src/graphql/getDocsByCompanies';
import { cleanRut, formatMoneyWithCurrency, formatRut } from 'src/utils/formatter';
import { parseFilterAmount } from 'src/utils/amountInput';
import LoadingSpinner from 'src/components/general/js/spinnerLoading'
import LoadingCard from 'src/components/general/js/loadingCard';
import { GenerateMultipleMatch } from 'src/graphql/conciliacion';
import { GetIndicatorByDate } from 'src/graphql/indicators';
import { importAlertState } from 'src/redux/actions/alert';
import { debounce } from 'src/utils/debounce';
import { setUpdateDocuments } from 'src/redux/actions/documentostributarios';
import { importUpdateTransactions } from 'src/redux/actions/conciliacion';
import { dispatchRefreshNominaLists } from 'src/utils/dispatchRefreshNominaLists';
import { importOpenSidebar } from 'src/redux/actions/openSidebar';
import { banksFormat } from 'src/utils/bankList';
import { Pagination, Dropdown, Checkbox, Button, Popup } from 'semantic-ui-react';
import FeatherIcon from 'feather-icons-react';
import 'src/containers/CompanyListComponent/CreateCompanyModal/CreateCompanyModal.scss';
import { useBlockedFeatures } from 'src/hooks/useBlockedFeatures';
import { GetInProcessNomina } from 'src/graphql/newNomina';
import {
  GetCreditCardStatementReconcileCandidates,
  ReconcileTransactionToCreditCardStatement,
} from 'src/graphql/statements';
import { NominaStatus } from 'src/utils/enum';
import { dt, isPeriodClosedError } from 'src/utils/documentsI18n';
import {
  pairFromMultipleInput,
  useModuleTcStatementPicker,
} from 'src/components/general/js/forms/reconcile/hooks/useModuleTcStatementPicker';
import ModuleTcStatementPickerModal from 'src/components/general/js/forms/reconcile/components/ModuleTcStatementPickerModal';

const isSpecialDocumentList = (types) =>
  types?.includes('NOMINAS') || types?.includes('CREDIT_CARD_STATEMENTS');

const BASE_LIMIT = 100;

/**
 * Helper function to detect if an error is a period locked error (critical)
 * These errors should stop processing immediately, not continue with next item
 */
const isPeriodLockedError = isPeriodClosedError;

const itemsPerPageOptions = [
  { key: 20, text: "20", value: 20 },
  { key: 50, text: "50", value: 50 },
  { key: 100, text: "100", value: 100 },
  { key: 200, text: "200", value: 200 },
];

const TableContent = ({ data, setOpenCustomModal, totalAmounts, setTotalAmounts, onPaginationRender, refreshTrigger, initialRutFilter, onInvoiceMatch, onBulkExchangeMatch, selectedTransactions, isMultiple, onSuccess }) => {
  const dispatch = useDispatch();
  const { isConciliacionBlocked, isAbonarBlocked } = useBlockedFeatures();
  // El botón de vincular/match debe mostrarse si el usuario puede conciliar
  // (movimientos/cartola) O abonar (doc↔obligación). El backend decide el
  // permiso final según el tipo de input (TRANSACTION → conciliacion; doc↔
  // obligación → abonar), incluso con el módulo conciliacion apagado.
  const canMatchOrAbonar = () => !isConciliacionBlocked() || !isAbonarBlocked();
  const [selectedRows, setSelectedRows] = useState([]); // Stores document IDs, not indices
  const [loadingButton, setLoadingButton] = useState(null);
  // Lock síncrono: setState de loading no alcanza a deshabilitar el botón en un doble click.
  const matchInFlightRef = useRef(false);
  const beginMatch = (key) => {
    if (matchInFlightRef.current) return false;
    matchInFlightRef.current = true;
    setLoadingButton(key);
    return true;
  };
  const endMatch = () => {
    matchInFlightRef.current = false;
    setLoadingButton(null);
  };
  const [selectedValues, setSelectedValues] = useState("NONE");
  const [documentTypes, setDocumentTypes] = useState(["TAX_DOCUMENT", "CREDIT", "OBLIGATION"]);
  const [obligationTypes, setObligationTypes] = useState([]);
  const { selectedCompany: { id: companyId } } = useSelector((state) => state.companyList);
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [inputErrors, setInputErrors] = useState({});
  const [showMoreFilters, setShowMoreFilters] = useState(true);
  const [closeDifferenceWithObligation, setCloseDifferenceWithObligation] = useState(false);
  const newRemainingAmounts = useRef({});
  
  // Track new row IDs for fade-in animation
  const [newRowIds, setNewRowIds] = useState(new Set());
  const previousRowIdsRef = useRef(new Set());

  const [limit, setLimit] = useState(BASE_LIMIT);
  const [filters, setFilters] = useState({ notPaid: true });
  const [excludeDocumentCodes, setExcludeDocumentCodes] = useState([]);
  // Local state for input values (for immediate UI updates without debounce delay)
  const [inputValues, setInputValues] = useState({
    docNum: '',
    name: '',
    filterRut: '',
    dateStart: '',
    dateEnd: '',
    amountStart: '',
    amountEnd: '',
  });
  const [variables, setVariables] = useState({
    companyId,
    isValid: true,
    pagination: { limit: BASE_LIMIT, sortAscending: false, page: 1 },
  });
  const [activePage, setActivePage] = useState(1);
  
  // Nomina-related state
  const [nominasDataFiltered, setNominasFiltered] = useState([]);
  const [nominaDetailIndex, setNominaDetailIndex] = useState(null);
  const [tcStatementDetailIndex, setTcStatementDetailIndex] = useState(null);

  // Verificar si data es una Transacción o un Documento
  // Si tiene documentType, es un documento
  // De lo contrario, verificar si tiene type === 'RECIEVED' o 'SPEND' (transacciones)
  const isTransaction = data?.documentType 
    ? false 
    : (data?.type === 'RECIEVED' || data?.type === 'SPEND');

  // Calculate currency date for exchange rate fetching (same logic as createObligationModal.jsx)
  const transactionDate = data?.stringDate || data?.date || data?.documentDate;
  const currencyDate = transactionDate
    ? (moment().isBefore(moment(transactionDate, data?.stringDate ? 'DD/MM/YYYY' : undefined))
        ? moment().format("DD/MM/YYYY")
        : moment(transactionDate, data?.stringDate ? 'DD/MM/YYYY' : undefined).format("DD/MM/YYYY"))
    : moment().format("DD/MM/YYYY");
  
  // Fetch exchange rate indicators when transaction is not in CLP
  const transactionCurrency = isTransaction ? (data?.currency || 'CLP') : (data?.currencyType || 'CLP');
  const isTransactionFx = isTransaction && transactionCurrency !== 'CLP';
  const shouldFetchExchangeRate = isTransactionFx;
  const getIndicatorByDate = GetIndicatorByDate(shouldFetchExchangeRate ? currencyDate : null);

  const { data: documentData, fetching, reexecuteQuery } = GetPossibleMatchDocuments(variables);
  const { executeMutation: generateMultipleMatch } = GenerateMultipleMatch();
  const tcPicker = useModuleTcStatementPicker(companyId);
  const pendingTcMatchRef = useRef(null);
  
  // Consulta de nóminas en proceso (para selección de tipo NOMINA)
  const nominasData = GetInProcessNomina({
    companyId,
    status: [NominaStatus.CREATED, NominaStatus.INFORMED, NominaStatus.BANKLOADED, NominaStatus.MANUAL],
    pagination: { limit: 100, page: 1 }
  });

  const showTcStatementFilters = isTransaction && data?.type === 'SPEND';
  const tcStatementsYear = useMemo(() => {
    if (!data?.date && !data?.stringDate) return new Date().getFullYear();
    const m = data?.stringDate
      ? moment(data.stringDate, 'DD/MM/YYYY')
      : moment(data.date);
    return m.isValid() ? m.year() : new Date().getFullYear();
  }, [data?.date, data?.stringDate]);

  const tcStatementsData = GetCreditCardStatementReconcileCandidates(
    { companyId, year: tcStatementsYear },
    !showTcStatementFilters || !documentTypes.includes('CREDIT_CARD_STATEMENTS'),
  );
  const { executeMutation: reconcileTxToTcStatement } =
    ReconcileTransactionToCreditCardStatement();

  const tcStatementsCandidates =
    tcStatementsData?.data?.creditCardStatementReconcileCandidates || [];

  // Algunos backends devuelven IDs de Mongo como `_id`, otros como `id`, y algunas filas de lista solo incluyen `documentId`.
  // GraphQL requiere estos IDs; si son undefined, JSON omite el campo y la mutación falla.
  const getDocId = (d) => {
    if (!d) return null;
    const raw =
      d.documentId ||
      d.docId ||
      d._id ||
      d.id ||
      d?.meta?.documentId ||
      d?.meta?.id;
    if (!raw) return null;
    if (typeof raw === 'string') return raw;
    // Handle Mongo-style objects like { $oid: "..." }
    if (typeof raw === 'object' && raw.$oid) return raw.$oid;
    return String(raw);
  };

  // Get exchange rate for transaction conversion to CLP
  const getTransactionExchangeRate = () => {
    // First try to use data.rate?.exchange if available
    if (data?.rate?.exchange) {
      const raw = data.rate.exchange;
      return typeof raw === 'number' ? raw : (raw[transactionCurrency] || 1);
    }
    
    // If not available and we have exchange rate data from API, use it
    if (shouldFetchExchangeRate && getIndicatorByDate.data?.getIndicatorByDate?.exchange?.[transactionCurrency]) {
      return getIndicatorByDate.data.getIndicatorByDate.exchange[transactionCurrency];
    }
    
    // Default to 1 if no exchange rate available
    return 1;
  };

  // Get transaction remaining amount converted to CLP
  const getTransactionRemainingInCLP = () => {
    // Manejar caso de múltiples transacciones
    if (isMultiple && selectedTransactions && selectedTransactions.length >= 1) {
      // Sumar los montos restantes de todas las transacciones seleccionadas
      return selectedTransactions.reduce((acc, transaction) => {
        const transactionRemaining = transaction.remaining !== undefined 
          ? transaction.remaining 
          : (transaction.meta?.remaining !== undefined ? transaction.meta?.remaining : (transaction.amount || transaction.meta?.amount || 0));
        const currency = transaction.currency || 'CLP';
        
        let remainingInCLP = transactionRemaining;
        if (currency !== 'CLP') {
          // Intentar usar la tasa de cambio de la transacción si está disponible
          const rate = transaction.rate?.exchange || 1;
          remainingInCLP = Math.round(transactionRemaining * rate);
        }
        
        return acc + Math.abs(remainingInCLP);
      }, 0);
    }
    
    if (!isTransaction) {
      // For documents, return the remaining as is
      return data?.meta?.remaining || 0;
    }
    
    const transactionRemaining = data?.remaining || 0;
    const currency = data?.currency || 'CLP';
    
    // If currency is CLP, return as is
    if (currency === 'CLP') {
      return transactionRemaining;
    }
    
    // Convert to CLP using exchange rate
    const exchangeRate = getTransactionExchangeRate();
    return Math.round(transactionRemaining * exchangeRate);
  };

  const getPerLineMaxCLP = (documentRemainingCLP) => {
    if (isTransactionFx) {
      return documentRemainingCLP;
    }
    const dataRemaining = getTransactionRemainingInCLP();
    return Math.min(documentRemainingCLP, dataRemaining || documentRemainingCLP);
  };

  const buildSelectedMatchLines = () => {
    const docs = documentData?.paginatedPossibleMatchsByDocument?.docs || [];
    return selectedRows
      .map((selectedDocId) => {
        const selectedDocument = docs.find((doc) => {
          const docId = doc.id || doc._id || `${doc.meta?.documentId || doc.meta?.number || ''}-${doc.documentDate || ''}`;
          return docId === selectedDocId;
        });
        if (!selectedDocument) return null;

        const docRemaining = selectedDocument.meta?.remaining;
        if (!docRemaining || docRemaining === 0) return null;

        const docRate = selectedDocument.meta?.rate || 1;
        const isDocObligation = selectedDocument?.documentType === 'OBLIGATION';
        const isDocCredit = selectedDocument?.documentType === 'CREDIT';
        const calculatedRemaining = (isDocObligation || isDocCredit)
          ? docRemaining
          : Math.round((docRemaining || 0) * docRate);

        const docId = selectedDocument?.id || selectedDocument?._id;
        const editedValue = editedValues[docId];
        const hasEditedValue = editedValue !== undefined && editedValue !== null && editedValue !== '' && !isNaN(parseFloat(editedValue)) && parseFloat(editedValue) >= 0;
        const amountCLP = Math.max(0, hasEditedValue ? parseFloat(editedValue) : calculatedRemaining);

        if (!amountCLP) return null;

        return {
          document: selectedDocument,
          amountCLP,
          docId,
        };
      })
      .filter(Boolean);
  };

  const selectedRowsIncludeNonCLPDocument = () => {
    const docs = documentData?.paginatedPossibleMatchsByDocument?.docs || [];
    return selectedRows.some((selectedDocId) => {
      const selectedDocument = docs.find((doc) => {
        const docId = doc.id || doc._id || `${doc.meta?.documentId || doc.meta?.number || ''}-${doc.documentDate || ''}`;
        return docId === selectedDocId;
      });
      if (!selectedDocument) return false;
      const docOriginalCurrency = selectedDocument?.meta?.exchange?.originalCurrency || selectedDocument?.currencyType || 'CLP';
      return docOriginalCurrency && docOriginalCurrency !== 'CLP';
    });
  };

  // Re-execute exchange rate query when currency date changes
  useEffect(() => {
    if (shouldFetchExchangeRate && currencyDate) {
      getIndicatorByDate.reexecuteQuery({ requestPolicy: 'network-only' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyDate, shouldFetchExchangeRate]);

  const totalDocs = documentData?.paginatedPossibleMatchsByDocument?.totalDocs || 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / (limit || 1)));
  let allNewRemaining = {};

  // When filtering by CREDIT, sort by N° Cuota (paymentNumber) ascending
  const docsToRender = useMemo(() => {
    const raw = documentData?.paginatedPossibleMatchsByDocument?.docs ?? [];
    if (selectedValues !== 'CREDIT') return raw;
    return [...raw].sort((a, b) => {
      const numA = Number(a?.meta?.paymentNumber);
      const numB = Number(b?.meta?.paymentNumber);
      return (numA || 0) - (numB || 0);
    });
  }, [documentData?.paginatedPossibleMatchsByDocument?.docs, selectedValues]);

  // Update pagination props in parent when they change
  useEffect(() => {
    if (onPaginationRender) {
      onPaginationRender({
        limit,
        setLimit,
        activePage,
        setActivePage,
        totalPages,
        totalDocs,
        itemsPerPageOptions
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, activePage, totalPages, totalDocs]);

  useEffect(() => {
    const normalizedTxType = String(data?.type || '').trim().toUpperCase();
    const coherentIsReceivedForQuery = isTransaction
      ? (normalizedTxType === 'SPEND')
      : undefined;
    const originRemaining = isTransaction
      ? Math.abs(Number(data?.remaining ?? data?.amount ?? 0) || 0)
      : Number(data?.meta?.remaining ?? data?.amount ?? 0);
    const originRut = isTransaction
      ? (normalizedTxType === 'SPEND' ? data?.receiver : data?.issuer)
      : (data?.meta?.isReceived ? data?.meta?.issuerId : data?.meta?.receiverId);

    setVariables({
      companyId,
      isValid: true,
      pagination: { limit, sortAscending: false, page: activePage },
      documentTypes,
      excludeDocumentIds: [data?.meta?.documentId || data?.meta?.number || data?.id || 'z'],
      excludeDocumentCodes,
      newFilter: {
        ...filters,
        obligationTypes,
        id: !isTransaction ? (data?.id || data?._id) : undefined,
        transactionType: isTransaction ? (data?.type || null) : undefined,
        isReceived: coherentIsReceivedForQuery,
        remaining: Number.isFinite(originRemaining) && originRemaining > 0
          ? originRemaining
          : undefined,
        rut: filters.filterRut ? undefined : (originRut || undefined),
      },
    });
  }, [companyId, activePage, limit, documentTypes, filters, obligationTypes, excludeDocumentCodes, data?.id, data?._id, data?.type, data?.remaining, data?.amount, data?.receiver, data?.issuer, data?.meta?.remaining, data?.meta?.issuerId, data?.meta?.receiverId, data?.meta?.isReceived, data?.meta?.documentId, data?.meta?.number, isTransaction]);

  // Set initial RUT filter when provided (e.g., when modal opens with a transaction)
  useEffect(() => {
    if (initialRutFilter) {
      // Update both inputValues and filters
      setInputValues((prev) => ({
        ...prev,
        filterRut: initialRutFilter,
      }));
      setFilters((prevFilters) => ({
        ...prevFilters,
        filterRut: initialRutFilter,
      }));
      setActivePage(1);
    }
  }, [initialRutFilter]);


  // Detect new rows when filtered results change
  useEffect(() => {
    const docs = documentData?.paginatedPossibleMatchsByDocument?.docs;
    if (!docs || docs.length === 0) {
      previousRowIdsRef.current = new Set();
      setNewRowIds(new Set());
      return;
    }

    // Get current row IDs as a Set (using item.id or item._id)
    const currentRowIds = new Set(
      docs.map((item) => item.id || item._id || `${item.meta?.documentId || item.meta?.number || ''}-${item.documentDate || ''}`)
    );

    // Only process if there are previous rows (skip on initial load)
    if (previousRowIdsRef.current.size > 0) {
      // Find new rows (exist in current but not in previous)
      const newIds = Array.from(currentRowIds).filter(id => !previousRowIdsRef.current.has(id));

      if (newIds.length > 0) {
        setNewRowIds(new Set(newIds));
        // Remove fade-in class after animation completes (600ms)
        const timer = setTimeout(() => {
          setNewRowIds(new Set());
        }, 600);

        // Update previous rows reference AFTER setting new rows
        previousRowIdsRef.current = currentRowIds;

        return () => clearTimeout(timer);
      }
    }

    // Update previous rows reference on every change
    previousRowIdsRef.current = currentRowIds;
  }, [documentData?.paginatedPossibleMatchsByDocument?.docs]);

  // Procesar datos de nóminas cuando se cargan - filtrar para mostrar solo nóminas con documentos que tengan remaining > 0
  useEffect(() => {
    if (nominasData?.data?.getNominasByCompany?.nominas?.length > 0) {
      const currentNominas = nominasData?.data?.getNominasByCompany?.nominas || [];
      
      const nominas = currentNominas.reduce((acc, nomina) => {
        const selectedDocuments = nomina?.selectedDocuments || [];
        
        // Filter documents that are APPROVED or PENDING and have remaining > 0
        const filteredDocuments = selectedDocuments.filter(
          doc => {
            const passesFilter = ['APPROVED', 'PENDING'].includes(doc.approvalStatus) && 
                   doc.selectedDTEDetail?.remaining > 0;
            return passesFilter;
          }
        );
        
        if (filteredDocuments.length > 0) {
          // Create a new object instead of mutating the original
          acc.push({
            ...nomina,
            selectedDocuments: filteredDocuments
          });
        }
        return acc;
      }, []);
      setNominasFiltered(nominas);
    } else {
      setNominasFiltered([]);
    }
  }, [nominasData.fetching, nominasData?.data?.getNominasByCompany?.nominas?.length, data, companyId]);

  // Re-obtener nóminas cuando los tipos de documento cambian a NOMINAS
  useEffect(() => {
    if (documentTypes.includes('NOMINAS')) {
      nominasData.reexecuteQuery({ requestPolicy: "network-only" });
    }
    if (documentTypes.includes('CREDIT_CARD_STATEMENTS')) {
      tcStatementsData.reexecuteQuery?.({ requestPolicy: 'network-only' });
    }
  }, [documentTypes]);

  const onValueChange = (e, { value }) => {
    const isDocumentType = ['TAX_DOCUMENT', 'CREDIT', 'OBLIGATION', 'BOLETA_HONORARIOS'].includes(value);
    const isNone = [""].includes(value);

    if (isNone) {
      setDocumentTypes(['TAX_DOCUMENT', 'CREDIT', 'OBLIGATION']);
      setObligationTypes([]);
      setFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters };
        delete updatedFilters.documentCodes;
        return updatedFilters;
      });
      setExcludeDocumentCodes([]);
    } else if (value === 'NOMINAS') {
      setDocumentTypes(['NOMINAS']);
      setObligationTypes([]);
      setSelectedRows([]);
      nominasData.reexecuteQuery({ requestPolicy: "network-only" });
    } else if (value === 'CREDIT_CARD_STATEMENTS') {
      setDocumentTypes(['CREDIT_CARD_STATEMENTS']);
      setObligationTypes([]);
      setSelectedRows([]);
      setTcStatementDetailIndex(null);
      tcStatementsData.reexecuteQuery?.({ requestPolicy: 'network-only' });
    } else if (value === 'BOLETA_HONORARIOS') {
      setDocumentTypes(['TAX_DOCUMENT']);
      setObligationTypes([]);
      setFilters((prevFilters) => ({
        ...prevFilters,
        documentCodes: ["0", "1"]
      }));
      setExcludeDocumentCodes([]);
    } else if (value === 'TAX_DOCUMENT') {
      setDocumentTypes([value]);
      setObligationTypes([]);
      setFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters };
        delete updatedFilters.documentCodes;
        return updatedFilters;
      });
      setExcludeDocumentCodes(["0", "1"]);
    } else if (isDocumentType) {
      setDocumentTypes([value]);
      setObligationTypes([]);
      setFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters };
        delete updatedFilters.documentCodes;
        return updatedFilters;
      });
      setExcludeDocumentCodes([]);
    } else {
      setDocumentTypes(['OBLIGATION']);
      setObligationTypes([value]);
      // Remove documentCodes filter when selecting obligation types
      setFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters };
        delete updatedFilters.documentCodes;
        return updatedFilters;
      });
      setExcludeDocumentCodes([]);
    }
    setSelectedValues(value);
    setActivePage(1);
  };

  // Debounced function to update filters (triggers API call)
  const updateFiltersDebounced = debounce((filterName, value) => {
    let parsedValue;
    
    if (filterName === "amountStart" || filterName === "amountEnd") {
      if (value === "" || value == null) {
        parsedValue = null;
      } else {
        parsedValue = parseFilterAmount(value);
      }
    } else if (filterName === "filterRut") {
      parsedValue = cleanRut(value);
    } else if (filterName === "docNum") {
      // Parse semicolon-separated folios into array
      if (value === "" || value == null) {
        parsedValue = null;
      } else {
        parsedValue = value
          .split(';')
          .map(folio => folio.trim())
          .filter(folio => folio.length > 0);
        // If no valid folios after parsing, set to null
        if (parsedValue.length === 0) {
          parsedValue = null;
        }
      }
    } else {
      parsedValue = value;
    }

    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };

      // Remove filter if value is empty, null, or invalid
      if (parsedValue === "" || parsedValue == null || parsedValue === undefined) {
        delete updatedFilters[filterName];
      } else {
        updatedFilters[filterName] = parsedValue;
      }

      setActivePage(1);

      setVariables((prevVariables) => ({
        ...prevVariables,
        newFilter: updatedFilters,
      }));

      return updatedFilters;
    });
  }, 800);

  // Handler for filter input changes - updates input value immediately, debounces filter update
  const handleFilterChange = (filterName, value) => {
    // Update input value immediately for smooth typing experience
    setInputValues((prev) => ({
      ...prev,
      [filterName]: value,
    }));

    // Debounce the actual filter update (triggers API call)
    updateFiltersDebounced(filterName, value);
  };

  // Handler to clear a specific filter
  const handleClearFilter = (filterName, e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    
    // Clear the input value immediately
    setInputValues((prev) => ({
      ...prev,
      [filterName]: '',
    }));

    // Clear the filter immediately (no debounce needed for clearing)
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      delete updatedFilters[filterName];

      setActivePage(1);

      setVariables((prevVariables) => ({
        ...prevVariables,
        newFilter: updatedFilters,
      }));

      return updatedFilters;
    });
  };

  // Helper function to get filter labels
  const getFilterLabel = (filterName) =>
    dt(`vincular.filterLabels.${filterName}`, { defaultValue: filterName });

  // Count active filters (excluding notPaid and empty values)
  const getActiveFilters = () => {
    const activeFilters = [];
    Object.keys(filters).forEach((key) => {
      if (key !== 'notPaid') {
        const value = filters[key];
        // Check if filter has a meaningful value
        if (value !== null && value !== undefined && value !== '') {
          // For numeric filters (amountStart, amountEnd), 0 is a valid value
          if (key === 'amountStart' || key === 'amountEnd') {
            if (value === 0 || (typeof value === 'number' && value > 0)) {
              activeFilters.push({ name: key, label: getFilterLabel(key) });
            }
          }
          // For dates, check if it's a valid date string
          else if (key === 'dateStart' || key === 'dateEnd') {
            if (value) {
              activeFilters.push({ name: key, label: getFilterLabel(key) });
            }
          }
          // For other filters (strings), check if not empty
          else if (typeof value === 'string' && value.trim() !== '') {
            activeFilters.push({ name: key, label: getFilterLabel(key) });
          }
          // For other types, include if truthy
          else if (value) {
            activeFilters.push({ name: key, label: getFilterLabel(key) });
          }
        }
      }
    });
    return activeFilters;
  };

  const activeFilters = getActiveFilters();
  const activeFiltersCount = activeFilters.length;

  const handleCheckboxChange = (index, item) => {
    setSelectedRows((prevSelectedRows) => {
      let updatedSelectedRows;
      let updatedTotalRemaining = totalAmounts;

      // Get document ID (use id or _id, whichever is available)
      const docId = item.id || item._id || `${item.meta?.documentId || item.meta?.number || ''}-${item.documentDate || ''}`;

      // If isMultiple is true, only allow one document to be selected
      if (isMultiple && prevSelectedRows.length > 0 && !prevSelectedRows.includes(docId)) {
        // Clear previous selection and select the new one
        const docs = documentData?.paginatedPossibleMatchsByDocument?.docs || [];
        const previousDocId = prevSelectedRows[0];
        const previousDoc = docs.find((doc) => {
          const prevDocId = doc.id || doc._id || `${doc.meta?.documentId || doc.meta?.number || ''}-${doc.documentDate || ''}`;
          return prevDocId === previousDocId;
        });
        
        if (previousDoc) {
          const prevDocRate = previousDoc.meta?.rate || 1;
          const prevIsObligation = previousDoc?.documentType === "OBLIGATION";
          const prevIsCredit = previousDoc?.documentType === "CREDIT";
          const prevNewRemaining = (prevIsObligation || prevIsCredit)
            ? previousDoc?.meta?.remaining 
            : Math.round((previousDoc.meta?.remaining || 0) * prevDocRate);
          const dataRemaining = getTransactionRemainingInCLP();
          const prevMaxAllowed = Math.min(prevNewRemaining, dataRemaining || prevNewRemaining);
          const prevDisplayValue = Math.min(prevNewRemaining, prevMaxAllowed);
          const prevDocIdForEdit = previousDoc?.id || previousDoc?._id;
          const prevCurrentRemainingAmount = parseFloat(editedValues[prevDocIdForEdit] !== undefined ? editedValues[prevDocIdForEdit] : prevDisplayValue);
          updatedTotalRemaining -= prevCurrentRemainingAmount;
        }
      }

      // Monto por conciliar should always be the remaining amount of the document
      // item.meta?.remaining already accounts for existing matches
      const docRate = item.meta?.rate || 1
      const isObligation = item?.documentType === "OBLIGATION"
      const isCredit = item?.documentType === "CREDIT"
      const newRemaining = (isObligation || isCredit)
        ? item?.meta?.remaining 
        : Math.round((item.meta?.remaining || 0) * docRate)

      // Get transaction/document remaining amount to limit the max value
      const maxAllowed = getPerLineMaxCLP(newRemaining);
      const displayValue = Math.min(newRemaining, maxAllowed);

      // Use edited value if available, otherwise use the capped display value
      const currentRemainingAmount = parseFloat(editedValues[docId] !== undefined ? editedValues[docId] : displayValue);

      // Check if document ID is already selected (not index)
      if (prevSelectedRows.includes(docId)) {
        updatedSelectedRows = prevSelectedRows.filter((selectedId) => selectedId !== docId);
        updatedTotalRemaining -= currentRemainingAmount;
      } else {
        // If isMultiple is true, replace previous selection with new one
        if (isMultiple && prevSelectedRows.length > 0) {
          updatedSelectedRows = [docId];
        } else {
          updatedSelectedRows = [...prevSelectedRows, docId];
        }
        updatedTotalRemaining += currentRemainingAmount;
      }

      setTotalAmounts(updatedTotalRemaining);
      return updatedSelectedRows;
    });
  };

  const isMultipleSelection = selectedRows.length >= 1;

  // Calculate total of selected documents' "Monto por conciliar" accounting for edited values
  const calculateSelectedTotal = () => {
    let total = 0;
    const docs = documentData?.paginatedPossibleMatchsByDocument?.docs || [];
    
    // Find selected documents by ID (not index)
    selectedRows.forEach((selectedDocId) => {
      const selectedDocument = docs.find((doc) => {
        const docId = doc.id || doc._id || `${doc.meta?.documentId || doc.meta?.number || ''}-${doc.documentDate || ''}`;
        return docId === selectedDocId;
      });
      
      if (!selectedDocument) return; // Document not in current filtered results

      const docRate = selectedDocument.meta?.rate || 1;
      const isObligation = selectedDocument?.documentType === "OBLIGATION";
      const isCredit = selectedDocument?.documentType === "CREDIT";
      
      const calculatedRemaining = (isObligation || isCredit)
        ? selectedDocument.meta?.remaining 
        : Math.round((selectedDocument.meta?.remaining || 0) * docRate);

      const maxAllowed = getPerLineMaxCLP(calculatedRemaining);
      const displayValue = Math.min(calculatedRemaining, maxAllowed);

      const docId = selectedDocument?.id || selectedDocument?._id;
      const editedValue = editedValues[docId];
      const hasEditedValue = editedValue !== undefined && editedValue !== null && editedValue !== '' && !isNaN(parseFloat(editedValue)) && parseFloat(editedValue) >= 0;
      
      // Use edited value if available, otherwise use the capped display value
      const newRemaining = hasEditedValue ? parseFloat(editedValue) : displayValue;
      total += Math.max(0, newRemaining || 0);
    });
    return total;
  };

  // Calculate difference for obligation checkbox
  const calculateObligationDifference = () => {
    // For transactions not in CLP, convert to CLP first
    const dataRemaining = getTransactionRemainingInCLP();
    const selectedTotal = calculateSelectedTotal();
    return Math.max(0, (dataRemaining || 0) - selectedTotal);
  };

  // Sync totalAmounts when filtered results change (remove selections for documents that disappeared)
  useEffect(() => {
    // Recalculate total based on currently visible selected documents
    const newTotal = calculateSelectedTotal();
    // Only update if different to avoid unnecessary re-renders
    if (Math.abs(newTotal - totalAmounts) > 0.01) {
      setTotalAmounts(newTotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData?.paginatedPossibleMatchsByDocument?.docs, selectedRows, editedValues]);

  // ==== FUNCIONES DE NÓMINA ====
  
  // Conciliar los documentos pendientes de una nómina en un solo generateMultipleMatch (1 tx → N docs)
  const matchNomina = async (nomina) => {
    if (!beginMatch(`match-nomina-${nomina.id}`)) return;
    let keepLocked = false;
    try {
    
    // data is the transaction we're matching against
    if (!isTransaction) {
      dispatch(
        importAlertState({
          content: dt('vincular.nominaOnlyBank'),
          state: "error",
          duration: 4,
        })
      );
      setLoadingButton(null);
      return;
    }

    const transactionId = getDocId(data);
    const documentPaymentDate = data.date || new Date();

    if (!transactionId) {
      dispatch(
        importAlertState({
          content: dt('vincular.missingTxId'),
          state: "error",
          duration: 6,
        })
      );
      setLoadingButton(null);
      return;
    }

    const pendingDocs = (nomina.selectedDocuments || []).filter((docNomina) => {
      const amount = Number(docNomina.amountSelected || 0) || 0;
      if (amount <= 0) return false;
      if (docNomina.isMatched === true) return false;
      if (docNomina.selectedDTEDetail?.isPaid === true) return false;
      const remaining = docNomina.selectedDTEDetail?.remaining;
      if (remaining !== undefined && remaining !== null && Number(remaining) <= 0) return false;
      const matchedAmount = Number(docNomina.matchedAmount || 0) || 0;
      return matchedAmount < amount;
    });

    if (!pendingDocs.length) {
      dispatch(
        importAlertState({
          content: dt('vincular.nominaNothingToMatch'),
          state: "warning",
          duration: 4,
        })
      );
      setLoadingButton(null);
      return;
    }

    const transactionCurrency = data?.currency || 'CLP';
    const txRemaining = Math.abs(Number(data?.remaining ?? data?.amount ?? 0) || 0);
    const origin = [{
      originType: "TRANSACTION",
      originId: transactionId,
      amount: txRemaining,
      originalAmount: txRemaining,
      currency: transactionCurrency,
      paymentDate: documentPaymentDate,
    }];
    const end = pendingDocs.map((docNomina) => {
      const amount = Number(docNomina.amountSelected || 0) || 0;
      const docCurrency = docNomina.currency || docNomina.currencyType || 'CLP';
      const endType = docNomina.selectedDTEDetail?.documentType || 'TAX_DOCUMENT';
      return {
        endType,
        endId: docNomina.documentId,
        amount,
        originalAmount: amount,
        currency: docCurrency,
        paymentDate: documentPaymentDate,
        nominaId: nomina.id,
        selectionId: docNomina.id,
      };
    });

    const invalidEnd = end.find((item) => !item?.endId);
    if (invalidEnd) {
      dispatch(
        importAlertState({
          content: dt('vincular.missingIds'),
          state: "error",
          duration: 6,
        })
      );
      setLoadingButton(null);
      return;
    }

    const input = {
      companyId,
      accountPlan: "NOPLAN",
      origin,
      end,
      nominaId: nomina.id,
      imputationMode: 'EXISTING_NOMINA',
    };

    const { error: matchError } = await generateMultipleMatch({ input });

    if (matchError) {
      const errorMessage = matchError.graphQLErrors?.[0]?.message || matchError.message || dt('alerts.matchError');
      const isPeriodLocked = isPeriodLockedError(errorMessage);
      dispatch(
        importAlertState({
          concept: isPeriodLocked ? dt('common.periodClosed') : undefined,
          content: errorMessage,
          state: isPeriodLocked ? "warning" : "error",
          duration: isPeriodLocked ? 6 : 4,
        })
      );
      setLoadingButton(null);
      return;
    }

    // After all documents are matched, trigger refetch
    dispatch(setUpdateDocuments({ value: true }));
    dispatch(importUpdateTransactions(true));
    dispatchRefreshNominaLists(dispatch);

    // Refetch nominas to update the list
    nominasData.reexecuteQuery({ requestPolicy: "network-only" });
    
    keepLocked = true;
    setOpenCustomModal(false);
    
    if (onSuccess) {
      onSuccess();
    }
    } finally {
      if (!keepLocked) endMatch();
    }
  };

  const matchCreditCardStatement = async (candidate) => {
    if (!beginMatch(`match-tc-statement-${candidate.statementId}`)) return;
    let keepLocked = false;
    try {

    if (!isTransaction || data?.type !== 'SPEND') {
      dispatch(
        importAlertState({
          content: dt('reconcile.cardStatementOnlyFromBankOutflow'),
          state: 'error',
          duration: 4,
        }),
      );
      setLoadingButton(null);
      return;
    }

    const transactionId = getDocId(data);
    if (!transactionId) {
      dispatch(
        importAlertState({
          content: dt('vincular.missingTxId'),
          state: 'error',
          duration: 6,
        }),
      );
      setLoadingButton(null);
      return;
    }

    const txRemaining = Math.abs(Number(data?.remaining ?? data?.amount ?? 0) || 0);
    const totalRemaining = Math.abs(Number(candidate.totalRemaining) || 0);
    if (txRemaining + 0.01 < totalRemaining) {
      dispatch(
        importAlertState({
          content:
            dt('reconcile.cardStatementMustCoverSum'),
          state: 'error',
          duration: 6,
        }),
      );
      setLoadingButton(null);
      return;
    }

    const { error: matchError, data: matchData } = await reconcileTxToTcStatement({
      input: {
        companyId,
        transactionId,
        statementId: candidate.statementId,
      },
    });

    if (matchError) {
      const errorMessage =
        matchError.graphQLErrors?.[0]?.message ||
        matchError.message ||
        dt('reconcile.cardStatementError');
      const isPeriodLocked = isPeriodLockedError(errorMessage);
      dispatch(
        importAlertState({
          concept: isPeriodLocked ? dt('common.periodClosed') : undefined,
          content: errorMessage,
          state: isPeriodLocked ? 'warning' : 'error',
          duration: isPeriodLocked ? 6 : 4,
        }),
      );
      setLoadingButton(null);
      return;
    }

    const remainingAfter =
      matchData?.reconcileTransactionToCreditCardStatement?.transactionRemainingAfter;
    dispatch(
      importAlertState({
        content:
          remainingAfter != null && remainingAfter > 0
            ? `Estado de cuenta conciliado. Remanente del movimiento: ${formatMoneyWithCurrency(remainingAfter, 'CLP')}.`
            : dt('reconcile.cardStatementOk'),
        state: 'success',
        duration: 4,
      }),
    );

    dispatch(setUpdateDocuments({ value: true }));
    dispatch(importUpdateTransactions(true));
    tcStatementsData.reexecuteQuery?.({ requestPolicy: 'network-only' });
    reexecuteQuery?.({ requestPolicy: 'network-only' });

    keepLocked = true;
    setOpenCustomModal(false);

    if (onSuccess) {
      onSuccess();
    }
    } finally {
      if (!keepLocked) endMatch();
    }
  };

  // ==== FIN DE FUNCIONES DE NÓMINA ====

  const match = async (document, newRemaining, exchangeData = null, creditCardStatementId = null) => {
    if (!beginMatch(`match-${document.id}`)) return;
    let keepLocked = false;
    try {
    
    // Check if this is multiple transactions match
    if (isMultiple && selectedTransactions && selectedTransactions.length > 1) {
      // Handle multiple transactions matching to one document
      const amount = Math.max(0, parseFloat(newRemaining) || 0);
      
      if (!amount || amount === 0) {
        dispatch(
          importAlertState({
            content: dt('vincular.amountMustBePositive'),
            state: "error",
            duration: 4,
          })
        );
        setLoadingButton(null);
        return;
      }

      // Build origin array with all transactions
      const origin = selectedTransactions.map(transaction => {
        const transactionId = transaction.id || transaction._id;
        const transactionCurrency = transaction.currency || 'CLP';
        const transactionRemaining = transaction.remaining !== undefined 
          ? transaction.remaining 
          : (transaction.meta?.remaining !== undefined ? transaction.meta?.remaining : (transaction.amount || transaction.meta?.amount || 0));
        
        return {
          originId: transactionId,
          originType: "TRANSACTION",
          currency: transactionCurrency,
          originalAmount: Math.abs(transactionRemaining),
          amount: Math.abs(transactionRemaining),
        };
      });
      
      // Build end array with single document
      const documentOriginalCurrency = document?.meta?.exchange?.originalCurrency || document?.currencyType || 'CLP';
      const end = [{
        endId: getDocId(document),
        endType: document?.documentType,
        currency: documentOriginalCurrency,
        originalAmount: amount,
        amount: amount
      }];

      const input = {
        companyId,
        accountPlan: "NOPLAN",
        origin,
        end
      };

      // Final safety: never send invalid GraphQL variables
      const invalidOrigin = (origin || []).find((o) => !o?.originId);
      const invalidEnd = (end || []).find((e) => !e?.endId);
      if (invalidOrigin || invalidEnd) {
        dispatch(
          importAlertState({
            content: dt('vincular.missingIds'),
            state: "error",
            duration: 6,
          })
        );
        setLoadingButton(null);
        return;
      }

      if (creditCardStatementId) {
        input.creditCardStatementId = creditCardStatementId
      } else {
        const { input: next, prompted } = await tcPicker.attachOrPrompt(
          pairFromMultipleInput(input),
          document.meta,
        )
        if (prompted) {
          pendingTcMatchRef.current = { document, newRemaining, exchangeData }
          return
        }
        if (next?.creditCardStatementId) input.creditCardStatementId = next.creditCardStatementId
      }

      const { data: matchData, error: matchError } = await generateMultipleMatch({ input });

      if (matchError) {
        const errorMessage = matchError.graphQLErrors?.[0]?.message || matchError.message || dt('alerts.matchError');
        const isPeriodLocked = isPeriodLockedError(errorMessage);
        dispatch(
          importAlertState({
            concept: isPeriodLocked ? dt('common.periodClosed') : undefined,
            content: errorMessage,
            state: isPeriodLocked ? "warning" : "error",
            duration: isPeriodLocked ? 6 : 4,
          })
        );
        setLoadingButton(null);
      } else {
        // Trigger refetch to get fresh data
        dispatch(setUpdateDocuments({ value: true }));
        dispatch(importUpdateTransactions(true));
        dispatchRefreshNominaLists(dispatch);

        keepLocked = true;
        setOpenCustomModal(false);
        
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }
      }
      return;
    }
    
    // Original single transaction/document matching logic
    // Check if data is a Transaction or a Document
    const isTransaction = data.type === 'RECIEVED' || data.type === 'SPEND';
    const dataRemaining = isTransaction ? data.remaining : data.meta?.remaining;
    const dataCurrency = isTransaction ? data.currency : data.currencyType;
    const dataRate = isTransaction ? 1 : (data.meta?.rate || 1);
    
    // Always use the "monto por conciliar" amount (newRemaining parameter)
    // This parameter already accounts for edited values and document type (OBLIGATION/CREDIT vs others)
    const amount = Math.max(0, parseFloat(newRemaining) || 0);
    
    if (!amount || amount === 0) {
      dispatch(
        importAlertState({
          content: dt('vincular.amountMustBePositive'),
          state: "error",
          duration: 4,
        })
      );
      setLoadingButton(null);
      return;
    }

    // Check if transaction or document is not in CLP - if so, always open ExchangeMatchModal
    const isTransactionNonCLP = isTransaction && dataCurrency && dataCurrency !== 'CLP';
    
    // Check document currency: use meta.exchange.originalCurrency if available, otherwise use currencyType
    // If meta.exchange.originalCurrency is undefined, default to CLP
    const documentOriginalCurrency = document?.meta?.exchange?.originalCurrency || document?.currencyType || 'CLP';
    const isDocumentNonCLP = documentOriginalCurrency && documentOriginalCurrency !== 'CLP';
    
    // Special case: if transaction is CLP and document has meta.exchange.originalCurrency that is not CLP, open modal
    // If meta.exchange.originalCurrency is undefined, default to CLP and match directly
    const shouldOpenExchangeModal = isTransactionNonCLP || isDocumentNonCLP;
    
    // If either transaction or document is not in CLP and no exchangeData was passed, trigger the parent modal swap
    if (shouldOpenExchangeModal && !exchangeData) {
      if (onInvoiceMatch) {
        onInvoiceMatch(document, amount);
      }
      setLoadingButton(null);
      return;
    }

    // Check if both transaction and document are non-CLP
    const isDataNonCLP = dataCurrency && dataCurrency !== 'CLP';
    // isDocumentNonCLP already declared above
    const bothNonCLP = isDataNonCLP && isDocumentNonCLP;

    // Prefer exchangeData when caller passed it (e.g. USD tx + CLP doc: bothNonCLP is false but FX is required)
    let exchangeRate = null;
    let localValue = null;
    if (exchangeData) {
      const er = parseFloat(exchangeData.exchangeRate);
      exchangeRate = Number.isFinite(er) && er > 0 ? er : null;
      if (exchangeData.localValue != null && exchangeData.localValue !== '') {
        const lv = Math.round(Number(exchangeData.localValue));
        localValue = Number.isFinite(lv) ? lv : null;
      }
      if (localValue == null && exchangeRate && amount) {
        localValue = Math.round(exchangeRate * amount);
      }
    }
    if (bothNonCLP) {
      // Try to get exchange rate from meta.exchangeRate, then meta.exchange.exchangeRate, then meta.rate
      exchangeRate = document?.meta?.exchangeRate || document?.meta?.exchange?.exchangeRate || document?.meta?.rate || 1;
      // Calculate localAmount as exchangeRate * "Monto por conciliar"
      localValue = Math.round(exchangeRate * amount);
    }

    let origin, end;
    
    if (isTransaction) {
      // Transaction is ORIGIN, selected document is END
      const originInput = {
        originId: getDocId(data),
        originType: "TRANSACTION",
        currency: dataCurrency || 'CLP',
        originalAmount: amount,
        amount: amount,
      };

      const docCur = document?.currencyType || 'CLP';
      const endAmountForDoc =
        isDataNonCLP && docCur === 'CLP' && localValue != null ? localValue : amount;

      const endInput = {
        endId: getDocId(document),
        endType: document?.documentType,
        currency: docCur,
        originalAmount: endAmountForDoc,
        amount: endAmountForDoc,
      };

      // Add exchange rate and local value if both are non-CLP or if exchangeData was provided
      if ((bothNonCLP || exchangeData) && exchangeRate && localValue) {
        endInput.exchangeRate = exchangeRate;
        endInput.localValue = localValue;
        endInput.matchCurrency = 'CLP';
        if (exchangeData) {
          endInput.paymentDate = document?.documentDate || new Date();
        }
      }

      origin = [originInput];
      end = [endInput];
    } else {
      // Document is ORIGIN, transaction is END
      const docCurOrigin = document?.currencyType || 'CLP';
      const originAmountForDoc =
        isDataNonCLP && docCurOrigin === 'CLP' && localValue != null ? localValue : amount;

      const originInput = {
        originId: getDocId(document),
        originType: document?.documentType,
        currency: docCurOrigin,
        originalAmount: originAmountForDoc,
        amount: originAmountForDoc,
      };

      const endInput = {
        endId: getDocId(data),
        endType: data.documentType,
        currency: dataCurrency || 'CLP',
        originalAmount: amount,
        amount: amount,
      };

      // Add exchange rate and local value if both are non-CLP or if exchangeData was provided
      if ((bothNonCLP || exchangeData) && exchangeRate && localValue) {
        originInput.exchangeRate = exchangeRate;
        originInput.localValue = localValue;
        originInput.matchCurrency = 'CLP';
        if (exchangeData) {
          originInput.paymentDate = document?.documentDate || new Date();
        }
      }

      origin = [originInput];
      end = [endInput];
    }

    const input = {
      companyId,
      accountPlan: "NOPLAN",
      origin,
      end
    };

    // Final safety: never send invalid GraphQL variables (prevents endId/originId missing errors)
    const invalidOrigin = (origin || []).find((o) => !o?.originId);
    const invalidEnd = (end || []).find((e) => !e?.endId);
    if (invalidOrigin || invalidEnd) {
      dispatch(
        importAlertState({
          content: dt('reconcile.missingIdentifiers'),
          state: "error",
          duration: 6,
        })
      );
      setLoadingButton(null);
      return;
    }

    if (creditCardStatementId) {
      input.creditCardStatementId = creditCardStatementId
    } else {
      const { input: next, prompted } = await tcPicker.attachOrPrompt(
        pairFromMultipleInput(input),
        document.meta,
      )
      if (prompted) {
        pendingTcMatchRef.current = { document, newRemaining, exchangeData }
        return
      }
      if (next?.creditCardStatementId) input.creditCardStatementId = next.creditCardStatementId
    }

    const { data: matchData, error: matchError } = await generateMultipleMatch({ input });

    if (matchError) {
      const errorMessage = matchError.graphQLErrors?.[0]?.message || matchError.message || dt('alerts.matchError');
      const isPeriodLocked = isPeriodLockedError(errorMessage);
      dispatch(
        importAlertState({
          concept: isPeriodLocked ? dt('common.periodClosed') : undefined,
          content: errorMessage,
          state: isPeriodLocked ? "warning" : "error",
          duration: isPeriodLocked ? 6 : 4,
        })
      );
      setLoadingButton(null);
    } else {
      // No success toast - only show error toast if something fails
      
      // Trigger refetch to get fresh data
      dispatch(setUpdateDocuments({ value: true }));
      dispatch(importUpdateTransactions(true));
      dispatchRefreshNominaLists(dispatch);

      keepLocked = true;
      setOpenCustomModal(false);
    }
    } finally {
      if (!keepLocked) endMatch();
    }
  };

  const matchMultiple = async () => {
    if (!beginMatch("match-multiple")) return;
    let keepLocked = false;
    try {
    
    // Get docs first before using it
    const docs = documentData?.paginatedPossibleMatchsByDocument?.docs || [];
    
    // Check if data is a Transaction or a Document
    const isTransaction = data.type === 'RECIEVED' || data.type === 'SPEND';
    const dataCurrency = isTransaction ? data.currency : data.currencyType;
    
    const isTransactionNonCLP = isTransaction && dataCurrency && dataCurrency !== 'CLP';
    const hasNonCLPDocuments = selectedRowsIncludeNonCLPDocument();

    if (hasNonCLPDocuments) {
      dispatch(
        importAlertState({
          content: 'Para documentos en moneda extranjera, debe conciliar individualmente.',
          state: 'error',
          duration: 4,
        })
      );
      setLoadingButton(null);
      return;
    }

    if (isTransactionNonCLP && onBulkExchangeMatch) {
      const matchLines = buildSelectedMatchLines();
      if (matchLines.length === 0) {
        dispatch(
          importAlertState({
            content: dt('reconcile.selectAtLeastOnePositive'),
            state: 'error',
            duration: 4,
          })
        );
        setLoadingButton(null);
        return;
      }

      const totalLocalCLP = matchLines.reduce((sum, line) => sum + line.amountCLP, 0);
      const transactionCLP = getTransactionRemainingInCLP();
      if (totalLocalCLP > transactionCLP) {
        dispatch(
          importAlertState({
            content: `El total seleccionado (${formatMoneyWithCurrency(totalLocalCLP, 'CLP')}) supera el saldo del movimiento (${formatMoneyWithCurrency(transactionCLP, 'CLP')}).`,
            state: 'error',
            duration: 5,
          })
        );
        setLoadingButton(null);
        return;
      }

      setLoadingButton(null);
      onBulkExchangeMatch({
        matchLines,
        totalLocalCLP,
        closeDifferenceWithObligation,
        differenceCLP: calculateObligationDifference(),
      });
      return;
    }
    
    // For transactions not in CLP, convert to CLP first
    const dataRemaining = getTransactionRemainingInCLP();
    const dataRate = isTransaction ? 1 : (data.meta?.rate || 1);
    
    // Validate that we have a valid amount
    if (!dataRemaining || dataRemaining === 0) {
      dispatch(
        importAlertState({
          content: dt('vincular.invalidReconcileAmount', {
            entity: isTransaction ? dt('vincular.entityTransaction') : dt('vincular.entityDocument'),
          }),
          state: "error",
          duration: 4,
        })
      );
      setLoadingButton(null);
      return;
    }
    
    // If data is a Transaction, it's the ORIGIN, and selected documents are END
    // If data is a Document, it's the END, and selected documents are ORIGIN
    // dataRemaining is already in CLP (converted by getTransactionRemainingInCLP)
    const dataAmount = dataRemaining;

    // Build the selected documents array (docs already defined above)
    const selectedDocuments = selectedRows.map((selectedDocId) => {
      // Find document by ID (not index)
      const selectedDocument = docs.find((doc) => {
        const docId = doc.id || doc._id || `${doc.meta?.documentId || doc.meta?.number || ''}-${doc.documentDate || ''}`;
        return docId === selectedDocId;
      });

      if (!selectedDocument) {
        return null; // Document not in current filtered results
      }

      // Monto por conciliar should always be the remaining amount of the document
      // selectedDocument.meta?.remaining already accounts for existing matches
      const docRemaining = selectedDocument.meta?.remaining;
      
      if (!docRemaining || docRemaining === 0) {
        return null;
      }
      
      const docRate = selectedDocument.meta?.rate || 1;
      const isDocObligation = selectedDocument?.documentType === "OBLIGATION";
      const isDocCredit = selectedDocument?.documentType === "CREDIT";
      
      // Calculate remaining - OBLIGATION and CREDIT use remaining directly, others adjust by rate
      let calculatedRemaining = (isDocObligation || isDocCredit)
        ? docRemaining 
        : Math.round((docRemaining || 0) * docRate);

      // PRIORITY: Use edited value if exists and is valid, otherwise use calculated value
      // Try both id and _id to ensure we find the edited value
      const docId = selectedDocument?.id || selectedDocument?._id;
      const editedValue = editedValues[docId];
      const hasEditedValue = editedValue !== undefined && editedValue !== null && editedValue !== '' && !isNaN(parseFloat(editedValue)) && parseFloat(editedValue) >= 0;
      const newRemaining = hasEditedValue
        ? parseFloat(editedValue)
        : calculatedRemaining;
      
      // Cap the amount at the transaction/document's remaining amount
      // This ensures we don't use more than what's available in the origin
      // When transaction is origin, cap each document at transaction remaining
      // When document is end, each selected document uses its own amount (not capped by end document)
      const maxAllowed = isTransaction 
        ? dataAmount  // Cap at transaction's remaining amount
        : newRemaining; // Don't cap when document is end (use document's own amount)
      const cappedRemaining = isTransaction 
        ? Math.min(newRemaining, maxAllowed)
        : newRemaining;
      
      // Use the capped remaining as the amount
      const amount = Math.max(0, cappedRemaining);

      if (!amount || amount === 0) {
        return null;
      }

      // Check if both transaction and document are non-CLP
      const isDataNonCLP = dataCurrency && dataCurrency !== 'CLP';
      // Check document currency: use meta.exchange.originalCurrency if available, otherwise use currencyType
      // If meta.exchange.originalCurrency is undefined, default to CLP
      const selectedDocOriginalCurrency = selectedDocument?.meta?.exchange?.originalCurrency || selectedDocument?.currencyType || 'CLP';
      const isDocumentNonCLP = selectedDocOriginalCurrency && selectedDocOriginalCurrency !== 'CLP';
      const bothNonCLP = isDataNonCLP && isDocumentNonCLP;

      // Get exchange rate from document's meta if both are non-CLP
      let exchangeRate = null;
      let localValue = null;
      if (bothNonCLP) {
        // Try to get exchange rate from meta.exchangeRate, then meta.exchange.exchangeRate, then meta.rate
        exchangeRate = selectedDocument?.meta?.exchangeRate || selectedDocument?.meta?.exchange?.exchangeRate || selectedDocument?.meta?.rate || 1;
        // Calculate localAmount as exchangeRate * "Monto por conciliar"
        localValue = Math.round(exchangeRate * amount);
      }

      return {
        id: docId,
        type: selectedDocument?.documentType,
        currency: selectedDocument?.currencyType || 'CLP',
        amount: amount,
        exchangeRate: exchangeRate,
        localValue: localValue,
      };
    }).filter(item => item !== null); // Remove any null entries

    // Validate that we have valid data
    if (selectedDocuments.length === 0) {
      dispatch(
        importAlertState({
          content: dt('vincular.noValidDocs'),
          state: "error",
          duration: 4,
        })
      );
      setLoadingButton(null);
      return;
    }

    // Ensure selected documents have ids (GraphQL requires endId/originId)
    const missingId = selectedDocuments.find((d) => !getDocId(d));
    if (missingId) {
      dispatch(
        importAlertState({
          content: dt('vincular.missingDocId'),
          state: "error",
          duration: 5,
        })
      );
      setLoadingButton(null);
      return;
    }

    // Construct origin and end based on whether data is a transaction or document
    // PRIORITY: Use edited amounts from selectedDocuments (which already includes edited values)
    let origin, end;
    
    // Calculate total of selected documents (using edited amounts)
    const totalSelectedAmount = selectedDocuments.reduce((sum, doc) => sum + doc.amount, 0);
    
    if (isTransaction) {
      // Transaction is ORIGIN, selected documents are END
      // Use the total of selected documents' amounts (what we're actually matching)
      // This should be capped at the transaction's remaining amount
      const transactionAmount = Math.min(totalSelectedAmount, dataAmount);

      origin = [{
        originId: getDocId(data),
        originType: "TRANSACTION",
        currency: dataCurrency,
        originalAmount: transactionAmount,
        amount: transactionAmount,
      }];
      
      // For each document, use the amount from selectedDocuments
      // If total exceeds transaction amount, proportionally reduce each document
      let remainingTransactionAmount = transactionAmount;
      end = selectedDocuments.map((doc, index) => {
        let docAmount = doc.amount;
        
        // If total selected exceeds transaction amount, proportionally reduce
        if (totalSelectedAmount > transactionAmount) {
          const proportion = doc.amount / totalSelectedAmount;
          docAmount = Math.round(transactionAmount * proportion * 100) / 100;
          // Last document gets any rounding difference to ensure total equals transaction amount
          if (index === selectedDocuments.length - 1) {
            docAmount = remainingTransactionAmount;
          } else {
            remainingTransactionAmount -= docAmount;
          }
        } else {
          // Use document amount as is (already capped at transaction remaining)
          remainingTransactionAmount -= docAmount;
        }
        
        const docId = getDocId(doc);
        if (!docId) {
          throw new Error(dt('reconcile.documentWithoutId'));
        }
        const endInput = {
          endId: docId,
          endType: doc.type || doc.documentType,
          currency: doc.currency,
          originalAmount: docAmount,
          amount: docAmount,
        };

        // Add exchange rate and local value if both are non-CLP
        if (doc.exchangeRate && doc.localValue) {
          // Recalculate localValue with the adjusted docAmount
          const adjustedLocalValue = Math.round(doc.exchangeRate * docAmount);
          endInput.exchangeRate = doc.exchangeRate;
          endInput.localValue = adjustedLocalValue;
          endInput.matchCurrency = 'CLP';
        }

        return endInput;
      });
    } else {
      // Document is END, selected documents are ORIGIN
      // For each document, use the amount from selectedDocuments (which already includes edited values)
      origin = selectedDocuments.map(doc => {
        const docId = getDocId(doc);
        if (!docId) {
          throw new Error(dt('reconcile.documentWithoutId'));
        }
        const originInput = {
          originId: docId,
          originType: doc.type || doc.documentType,
          currency: doc.currency,
          originalAmount: doc.amount,
          amount: doc.amount,
        };

        // Add exchange rate and local value if both are non-CLP
        if (doc.exchangeRate && doc.localValue) {
          originInput.exchangeRate = doc.exchangeRate;
          originInput.localValue = doc.localValue;
          originInput.matchCurrency = 'CLP';
        }

        return originInput;
      });
      
      // Use the document's remaining amount (not the sum of selected documents)
      // The document amount should be the minimum of document remaining and total selected
      const documentAmount = Math.min(dataAmount, totalSelectedAmount);
      
      end = [{
        endId: getDocId(data),
        endType: data.documentType,
        currency: dataCurrency,
        originalAmount: documentAmount,
        amount: documentAmount,
      }];
    }

    // Calculate difference for close obligation
    const difference = calculateObligationDifference();
    
    // Determine isReceived based on transaction type
    // isReceived should be true if transaction type is SPEND, false if RECIEVED
    const isReceived = isTransaction 
      ? (data.type === 'SPEND') 
      : true; // Default to true for documents if not a transaction

    const normalizeOblType = (t) =>
      String(t || '').toLowerCase().replace(/_/g, '');
    const isTraspasoFxRendicion =
      normalizeOblType(data?.meta?.type) === 'rendicionportraspasoentrecuentas' ||
      selectedRows.some((selectedDocId) => {
        const selectedDocument = docs.find((doc) => {
          const docId =
            doc.id ||
            doc._id ||
            `${doc.meta?.documentId || doc.meta?.number || ''}-${doc.documentDate || ''}`;
          return docId === selectedDocId;
        });
        return (
          normalizeOblType(selectedDocument?.meta?.type) ===
          'rendicionportraspasoentrecuentas'
        );
      });
    
    const input = {
      companyId,
      accountPlan: "NOPLAN",
      origin,
      end,
      close: closeDifferenceWithObligation && difference > 0 ? {
        shouldBeClose: true,
        amount: difference,
        type: isTraspasoFxRendicion ? "EXCHANGE_RATE_DIFFERENCE" : "DIFFERENCE",
        isReceived: isReceived
      } : undefined
    };

    const { data: matchData, error: matchError } = await generateMultipleMatch({ input });

    if (matchError) {
      const errorMessage = matchError.graphQLErrors?.[0]?.message || matchError.message || dt('alerts.matchError');
      const isPeriodLocked = isPeriodLockedError(errorMessage);
      dispatch(
        importAlertState({
          concept: isPeriodLocked ? dt('common.periodClosed') : undefined,
          content: errorMessage,
          state: isPeriodLocked ? "warning" : "error",
          duration: isPeriodLocked ? 6 : 4,
        })
      );
      setLoadingButton(null);
    } else {
      // No success toast - only show error toast if something fails
      
      // Trigger refetch to get fresh data
      dispatch(setUpdateDocuments({ value: true }));
      dispatch(importUpdateTransactions(true));
      dispatchRefreshNominaLists(dispatch);

      keepLocked = true;
      setOpenCustomModal(false);
    }

    allNewRemaining = {};
    } finally {
      if (!keepLocked) endMatch();
    }
  };

  const handleEditStart = (index, item) => {
    const docId = item.id || item._id || `${item.meta?.documentId || item.meta?.number || ''}-${item.documentDate || ''}`;
    if (selectedRows.includes(docId)) {
      handleCheckboxChange(index, item);
    }
    setEditingIndex(index);
  };

  const handleEditEnd = (index, item) => {
    // Monto por conciliar should always be the remaining amount of the document
    // item.meta?.remaining already accounts for existing matches
    const docRate = item.meta?.rate || 1
    const isObligation = item?.documentType === "OBLIGATION"
    const isCredit = item?.documentType === "CREDIT"
    const newRemaining = (isObligation || isCredit)
      ? item?.meta?.remaining 
      : Math.round((item.meta?.remaining || 0) * docRate)

    const dataRemaining = getTransactionRemainingInCLP();
    const maxAllowed = isTransactionFx
      ? newRemaining
      : Math.min(newRemaining, dataRemaining || newRemaining);
    const maxCurrency = isTransactionFx ? 'CLP' : (data?.currency || data?.currencyType || 'CLP');

    const newValue = parseFloat(newRemainingAmounts.current[item.id].value);
    if (newValue > maxAllowed || newValue < 0) {
      setInputErrors((prev) => ({
        ...prev,
        [item.id]: `El monto no puede ser mayor que ${formatMoneyWithCurrency(maxAllowed, maxCurrency)} (monto disponible) o menor a 0.`,
      }));
      dispatch(
        importAlertState({
          content: dt('vincular.amountExceedsMax', {
            max: formatMoneyWithCurrency(maxAllowed, maxCurrency),
          }),
          state: "error",
          duration: 4,
        })
      );
      return; 
    }
    setEditedValues((prev) => ({
      ...prev,
      [item.id]: newValue
    }));
    setInputErrors((prev) => ({
      ...prev,
      [item.id]: null,
    }));
    setEditingIndex(null);
  };

  const handleCancelEdit = (index, item) => {
    // Revert to original value - remove the edited value to show original
    setEditedValues((prev) => {
      const newValues = { ...prev };
      delete newValues[item.id];
      return newValues;
    });
    setInputErrors((prev) => ({
      ...prev,
      [item.id]: null,
    }));
    setEditingIndex(null);
  }

  const handleModuleTcPickerConfirm = async (statementId) => {
    const next = tcPicker.confirm(statementId)
    const pending = pendingTcMatchRef.current
    pendingTcMatchRef.current = null
    if (!pending || !next?.creditCardStatementId) return
    await match(
      pending.document,
      pending.newRemaining,
      pending.exchangeData,
      next.creditCardStatementId,
    )
  }

  return (
    <>
    <div className="vincular-documento-table-content-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div className="vincular-documento-table-scrollable-area" name="settle-document-modal" style={{ padding: '20px', flex: '1 1 auto', overflowY: 'auto', overflowX: 'visible', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flexShrink: 0 }}>
          {/* First Row: Tipo de Documento + Ver más filtros + Button */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', width: '100%', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ overflow: 'visible', zIndex: 100, position: 'relative', width: '50%', flexShrink: 0 }}>
              <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 600 }} htmlFor="document-type">TIPO DE DOCUMENTO</label>
              <ObligationsDropdown
                value={selectedValues}
                onChange={onValueChange}
                includeDocumentTypes={true}
                includeRendiciones={true}
                showNominas={isTransaction && data?.type === 'SPEND'}
                showCreditCardStatements={showTcStatementFilters}
                placeholder={dt('vincular.selectDocType')}
                id="document-type"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, flexWrap: 'wrap' }}>
              <div 
                onClick={() => setShowMoreFilters(!showMoreFilters)}
                style={{ 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  paddingBottom: '8px',
                  color: '#6B7280',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
                className="tw-text-xs tw-text-gray-600"
              >
                <span>
                  Ver más filtros{activeFiltersCount > 0 ? ` (${activeFiltersCount} activo${activeFiltersCount !== 1 ? 's' : ''})` : ''}
                </span>
                <FeatherIcon 
                  icon={showMoreFilters ? 'chevron-up' : 'chevron-down'}
                  size={14}
                />
              </div>
              {activeFiltersCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {activeFilters.map((filter) => (
                    <span
                      key={filter.name}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 6px 2px 8px',
                        backgroundColor: '#E3EEF2',
                        color: '#3C4B64',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {filter.label}
                      <span
                        onClick={(e) => handleClearFilter(filter.name, e)}
                        style={{
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginLeft: '2px',
                          padding: '0 2px',
                          borderRadius: '2px',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#C8D8E0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title={dt('vincular.removeFilter')}
                      >
                        <FeatherIcon icon="x" size={12} />
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Additional Filters - Collapsible */}
          {showMoreFilters && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              {/* Second Row: Folio, Nombre, RUT */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '10px' }}>
                <div>
                  <Popup
                    content={dt('reconcile.folioSearchHint')}
                    position="top center"
                    inverted
                    trigger={
                      <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 600 }} htmlFor="doc-num">
                        FOLIO
                        <span style={{ marginLeft: '5px', color: '#4183c4', cursor: 'help' }}>ⓘ</span>
                      </label>
                    }
                  />
                  <input
                    id="doc-num"
                    type="text"
                    placeholder={dt('filters.fields.folioPlaceholder')}
                    name="docNum"
                    value={inputValues.docNum}
                    onChange={(e) => handleFilterChange('docNum', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 600 }} htmlFor="name-filter">NOMBRE</label>
                  <input
                    id="name-filter"
                    type="text"
                    placeholder={dt('vincular.businessNamePlaceholder')}
                    name="name"
                    value={inputValues.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 600 }} htmlFor="rut-filter">RUT</label>
                  <input
                    id="rut-filter"
                    type="text"
                    placeholder={dt('vincular.rutPlaceholder')}
                    name="filterRut"
                    value={inputValues.filterRut}
                    onChange={(e) => handleFilterChange('filterRut', e.target.value)}
                    maxLength={12}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              
              {/* Third Row: Desde, Hasta, Monto Mínimo, Monto Máximo */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 600 }} htmlFor="date-start">DESDE</label>
                  <input
                    id="date-start"
                    type="date"
                    value={inputValues.dateStart}
                    onChange={(e) => handleFilterChange('dateStart', e.target.value)}
                    name="dateStart"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 600 }} htmlFor="date-end">HASTA</label>
                  <input
                    id="date-end"
                    type="date"
                    value={inputValues.dateEnd}
                    onChange={(e) => handleFilterChange('dateEnd', e.target.value)}
                    name="dateEnd"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 600 }} htmlFor="amount-start">{dt('reconcile.amountMinShort')}</label>
                  <input
                    id="amount-start"
                    type="number"
                    placeholder={dt('vincular.amountMin')}
                    name="amountStart"
                    value={inputValues.amountStart}
                    onChange={(e) => handleFilterChange('amountStart', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 600 }} htmlFor="amount-end">{dt('reconcile.amountMaxShort')}</label>
                  <input
                    id="amount-end"
                    type="number"
                    placeholder={dt('vincular.amountMax')}
                    name="amountEnd"
                    value={inputValues.amountEnd}
                    onChange={(e) => handleFilterChange('amountEnd', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="tw-overflow-hidden tw-mt-4 tw-flex tw-flex-col" style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Action Row - Button and Checkbox */}
          <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4 tw-px-2" style={{ flexShrink: 0 }}>
            {canMatchOrAbonar() && !isSpecialDocumentList(documentTypes) && (
              <Button
                type="button"
                color="blue"
                size="mini"
                onClick={() => {
                  if (matchInFlightRef.current) return;
                  matchMultiple();
                }}
                disabled={
                  isMultiple ||
                  !isMultipleSelection ||
                  !!loadingButton ||
                  calculateSelectedTotal() > getTransactionRemainingInCLP() ||
                  (isTransactionFx && selectedRowsIncludeNonCLPDocument())
                }
                loading={loadingButton === "match-multiple"}
                style={{ minWidth: '150px', flexShrink: 0 }}
                title={
                  calculateSelectedTotal() > getTransactionRemainingInCLP()
                    ? `El total seleccionado supera el saldo del movimiento (${formatMoneyWithCurrency(getTransactionRemainingInCLP(), 'CLP')})`
                    : isTransactionFx && selectedRowsIncludeNonCLPDocument()
                      ? 'No se puede conciliar masivamente documentos en moneda extranjera.'
                      : ''
                }
              >
                {dt('vincular.reconcileBulk')}
                <i className="arrow up right icon" style={{ marginLeft: '0.5em', transform: 'rotate(45deg)' }}></i>
              </Button>
            )}
            {isMultipleSelection && !isSpecialDocumentList(documentTypes) && calculateObligationDifference() > 0 && (
              <Checkbox
                checked={closeDifferenceWithObligation}
                onChange={(e, { checked }) => setCloseDifferenceWithObligation(checked)}
                label={`Cerrar diferencia con una obligación por ${formatMoneyWithCurrency(calculateObligationDifference(), isTransactionFx ? 'CLP' : (data?.currency || data?.currencyType || 'CLP'))}`}
              />
            )}
          </div>
          
          {/* Header Row */}
          {!isSpecialDocumentList(documentTypes) && (
          <div className="!tw-block scfo-grid-table-header !tw-h-auto tw-overflow-hidden tw-rounded-md tw-w-full tw-sticky tw-top-0 tw-z-10" style={{ flexShrink: 0 }}>
            <div className="tw-w-full tw-flex tw-py-2 tw-min-w-0">
              <div className="tw-w-full tw-flex tw-py-1 tw-items-center tw-min-w-0">
                <div className="tw-w-[40px] tw-flex tw-justify-center tw-items-center tw-flex-shrink-0"></div>
                <div className="tw-w-full tw-flex tw-items-center tw-min-w-0" style={{ paddingLeft: '10px' }}>
                  <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-flex-shrink-0 tw-text-left" style={{ width: '170px' }}>{dt('table.headers.type')}</div>
                  {selectedValues === 'CREDIT' ? (
                    <div className="tw-flex tw-items-center tw-flex-grow tw-min-w-0">
                      <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-text-left tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap" style={{ flex: '0 0 30%', minWidth: 0 }}>{dt('reconcile.bankColumn')}</div>
                      <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-text-left tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap" style={{ flex: '0 0 15%', minWidth: 0 }}>Nº Cuota</div>
                      <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-text-left tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap" style={{ flex: '1 1 55%', minWidth: 0 }}>Identificador</div>
                    </div>
                  ) : (
                    <div className="tw-flex tw-items-center tw-flex-grow tw-min-w-0">
                      {selectedValues !== 'invoice' && (
                        <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-text-left tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap" style={{ flex: '0 0 20%', minWidth: 0 }}>Rut</div>
                      )}
                      <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-text-left tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap" style={{ flex: '1 1 65%', minWidth: 0 }}>{dt('table.headers.businessName')}</div>
                      <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-text-left tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap" style={{ flex: '0 0 15%', minWidth: 0 }}>Folio</div>
                    </div>
                  )}
                  <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-flex-shrink-0 tw-text-left" style={{ width: '95px' }}>{dt('table.headers.issueDate')}</div>
                  {selectedValues === 'invoice' && (
                    <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-flex-shrink-0 tw-text-left" style={{ width: '120px' }}>Moneda Origen</div>
                  )}
                  <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-flex-shrink-0 tw-text-left" style={{ width: '110px' }}>{dt('table.headers.amount')}</div>
                  <div className="scfo-grid-table-header__col tw-py-1 tw-px-2 tw-flex-shrink-0 tw-whitespace-nowrap tw-text-left" style={{ width: '300px' }}>{dt('vincular.amountToReconcile')}</div>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Nomina Table - Show when NOMINA is selected */}
          {documentTypes.includes('NOMINAS') && (
            <div className="tw-space-y-4 tw-py-2 tw-overflow-y-auto tw-overflow-x-hidden" style={{ flex: 1, minHeight: '200px' }}>
              {nominasData.fetching ? (
                <div className="tw-w-full tw-space-y-2">
                  <LoadingCard height={"60px"} quantity={3} />
                </div>
              ) : nominasDataFiltered.length === 0 ? (
                <div className="tw-w-full tw-flex tw-py-4 tw-border-solid tw-border-[1px] tw-border-[#e3eef2] tw-rounded-md tw-shadow-md" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                  <div className="tw-w-full tw-text-center tw-text-gray-500 tw-flex tw-items-center tw-justify-center">
                    No tienes nóminas en proceso con documentos disponibles para conciliar
                  </div>
                </div>
              ) : (
                nominasDataFiltered.map((nomina, nominaIndex) => (
                  <div key={nomina.id} className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                    {/* Nomina Header - with Conciliar button */}
                    <div 
                      className="scfo-grid-table-header scfo-grid-table-header--flush tw-p-3 tw-flex tw-items-center tw-justify-between"
                    >
                      <div 
                        className="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer tw-flex-1"
                        onClick={() => setNominaDetailIndex(nominaDetailIndex === nominaIndex ? null : nominaIndex)}
                      >
                        <div>
                          <div className="tw-font-semibold tw-text-sm">{nomina.name || `Nómina #${nomina.id?.substring(0, 8)}`}</div>
                          <div className="tw-text-xs tw-text-white/80">
                            {nomina.selectedDocuments?.length || 0} documento(s) • 
                            Total: {formatMoneyWithCurrency(
                              nomina.selectedDocuments?.reduce((acc, doc) => acc + (doc.amountSelected || 0), 0) || 0,
                              'CLP'
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <Button
                          type="button"
                          color="blue"
                          size="mini"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (matchInFlightRef.current) return;
                            matchNomina(nomina);
                          }}
                          loading={loadingButton === `match-nomina-${nomina.id}`}
                          disabled={!!loadingButton}
                        >
                          {dt('vincular.reconcileNomina')}
                          <i className="arrow up right icon" style={{ marginLeft: '0.5em', transform: 'rotate(45deg)' }}></i>
                        </Button>
                        <div 
                          className="tw-cursor-pointer tw-p-1 hover:tw-bg-white/10 tw-rounded"
                          onClick={() => setNominaDetailIndex(nominaDetailIndex === nominaIndex ? null : nominaIndex)}
                        >
                          <FeatherIcon 
                            icon={nominaDetailIndex === nominaIndex ? 'chevron-up' : 'chevron-down'} 
                            size={18}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Nomina Documents - Expanded (read-only, just to see what's inside) */}
                    {nominaDetailIndex === nominaIndex && (
                      <div className="tw-bg-white">
                        {/* Documents Header */}
                        <div className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-px-4 tw-py-2 tw-flex tw-items-center tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase">
                          <div style={{ width: '150px' }}>{dt('table.headers.type')}</div>
                          <div style={{ width: '120px' }}>RUT</div>
                          <div style={{ flex: 1 }}>{dt('table.headers.businessName')}</div>
                          <div style={{ width: '90px' }}>Folio</div>
                          <div style={{ width: '110px' }}>{dt('table.headers.amount')}</div>
                        </div>
                        
                        {/* Document Rows (read-only) */}
                        {nomina.selectedDocuments?.map((doc) => (
                          <div 
                            key={doc.id} 
                            className="tw-px-4 tw-py-3 tw-flex tw-items-center tw-border-b tw-border-gray-100 last:tw-border-b-0"
                          >
                            <div style={{ width: '150px' }} className="tw-text-sm tw-truncate">
                              {doc.selectedDTEDetail?.typeLabel || doc.selectedDTEDetail?.summary || '-'}
                            </div>
                            <div style={{ width: '120px' }} className="tw-text-sm tw-truncate">
                              {formatRut(doc.selectedDTEDetail?.issuerId) || '-'}
                            </div>
                            <div style={{ flex: 1 }} className="tw-text-sm tw-truncate tw-pr-2">
                              {doc.selectedDTEDetail?.issuerName || '-'}
                            </div>
                            <div style={{ width: '90px' }} className="tw-text-sm tw-text-blue-600">
                              {doc.selectedDTEDetail?.documentId || '-'}
                            </div>
                            <div style={{ width: '110px' }} className="tw-text-sm">
                              {formatMoneyWithCurrency(doc.amountSelected || 0, 'CLP')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Credit Card Statement Table */}
          {documentTypes.includes('CREDIT_CARD_STATEMENTS') && (
            <div className="tw-space-y-4 tw-py-2 tw-overflow-y-auto tw-overflow-x-hidden" style={{ flex: 1, minHeight: '200px' }}>
              {tcStatementsData.fetching ? (
                <div className="tw-w-full tw-space-y-2">
                  <LoadingCard height={"60px"} quantity={3} />
                </div>
              ) : tcStatementsCandidates.length === 0 ? (
                <div className="tw-w-full tw-flex tw-py-4 tw-border-solid tw-border-[1px] tw-border-[#e3eef2] tw-rounded-md tw-shadow-md" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                  <div className="tw-w-full tw-text-center tw-text-gray-500 tw-flex tw-items-center tw-justify-center">
                    No hay estados de cuenta de tarjeta con obligaciones TC pendientes para conciliar
                  </div>
                </div>
              ) : (
                tcStatementsCandidates.map((candidate, candidateIndex) => (
                  <div key={candidate.statementId} className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                    <div className="scfo-grid-table-header scfo-grid-table-header--flush tw-p-3 tw-flex tw-items-center tw-justify-between">
                      <div
                        className="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer tw-flex-1"
                        onClick={() =>
                          setTcStatementDetailIndex(
                            tcStatementDetailIndex === candidateIndex ? null : candidateIndex,
                          )
                        }
                      >
                        <div>
                          <div className="tw-font-semibold tw-text-sm">
                            {candidate.cardLabel || 'Tarjeta'} — {candidate.periodKey || '-'}
                          </div>
                          <div className="tw-text-xs tw-text-white/80">
                            {candidate.obligations?.length || 0} obligación(es) TC • Total:{' '}
                            {formatMoneyWithCurrency(candidate.totalRemaining || 0, 'CLP')}
                          </div>
                        </div>
                      </div>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <Button
                          type="button"
                          color="blue"
                          size="mini"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (matchInFlightRef.current) return;
                            matchCreditCardStatement(candidate);
                          }}
                          loading={loadingButton === `match-tc-statement-${candidate.statementId}`}
                          disabled={!!loadingButton}
                        >
                          Conciliar
                          <i className="arrow up right icon" style={{ marginLeft: '0.5em', transform: 'rotate(45deg)' }}></i>
                        </Button>
                        <div
                          className="tw-cursor-pointer tw-p-1 hover:tw-bg-white/10 tw-rounded"
                          onClick={() =>
                            setTcStatementDetailIndex(
                              tcStatementDetailIndex === candidateIndex ? null : candidateIndex,
                            )
                          }
                        >
                          <FeatherIcon
                            icon={tcStatementDetailIndex === candidateIndex ? 'chevron-up' : 'chevron-down'}
                            size={18}
                          />
                        </div>
                      </div>
                    </div>

                    {tcStatementDetailIndex === candidateIndex && (
                      <div className="tw-bg-white">
                        <div className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-px-4 tw-py-2 tw-flex tw-items-center tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase">
                          <div style={{ width: '200px' }}>{dt('table.headers.type')}</div>
                          <div style={{ flex: 1 }}>{dt('detail.descriptionField')}</div>
                          <div style={{ width: '120px' }}>Remanente</div>
                        </div>
                        {candidate.obligations?.map((obl) => (
                          <div
                            key={obl.id}
                            className="tw-px-4 tw-py-3 tw-flex tw-items-center tw-border-b tw-border-gray-100 last:tw-border-b-0"
                          >
                            <div style={{ width: '200px' }} className="tw-text-sm tw-truncate">
                              {obl.typeLabel || obl.type || '-'}
                            </div>
                            <div style={{ flex: 1 }} className="tw-text-sm tw-truncate tw-pr-2">
                              {obl.description || '-'}
                            </div>
                            <div style={{ width: '120px' }} className="tw-text-sm">
                              {formatMoneyWithCurrency(obl.remaining || 0, 'CLP')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Table Body - Show when special lists are NOT selected */}
          {!isSpecialDocumentList(documentTypes) && (
          <div className="tw-space-y-2 tw-py-2 tw-overflow-y-auto tw-overflow-x-hidden" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {fetching ? (
                  <div className="tw-w-full tw-space-y-2">
                    <LoadingCard height={"60px"} quantity={5} />
                  </div>
                ) : !docsToRender?.length ? (
                  <div className="tw-w-full tw-flex tw-py-2 tw-border-solid tw-border-[1px] tw-border-[#e3eef2] tw-rounded-md tw-shadow-md" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                    <div className="tw-w-full tw-text-center tw-text-gray-500 tw-flex tw-items-center tw-justify-center">
                      No tienes documentos disponibles o tienes filtros activos
                    </div>
                  </div>
                )
                  :
                  (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                      {docsToRender
                        .map((item, index) => {
                        // Monto por conciliar should always be the remaining amount of the document
                        // item.meta?.remaining already accounts for existing matches
                        const docRate = item.meta?.rate || 1
                        const isObligation  = item?.documentType === "OBLIGATION"
                        const isCredit = item?.documentType === "CREDIT"
                        // For obligations and credits, use remaining directly; for others, apply rate
                        const newRemaining = (isObligation || isCredit)
                          ? item?.meta?.remaining 
                          : Math.round((item.meta?.remaining || 0) * docRate)

                        if (newRemaining <= 0) return

                        // Get transaction/document remaining amount to limit the max value
                        // For transactions not in CLP, convert to CLP first
                        const dataRemaining = getTransactionRemainingInCLP();
                        const maxAllowed = getPerLineMaxCLP(newRemaining);
                        const displayValue = Math.min(newRemaining, maxAllowed);

                        const rowAmountCurrency = isTransactionFx ? 'CLP' : (data?.currency || data?.currencyType || 'CLP');

                        allNewRemaining[index] = newRemaining

                        // Generate unique row ID for fade-in detection
                        const rowId = item.id || item._id || `${item.meta?.documentId || item.meta?.number || ''}-${item.documentDate || ''}`;
                        const isNewRow = newRowIds.has(rowId);

                        return (
                          <div key={rowId} className={`tw-w-full tw-flex tw-py-2 tw-border-solid tw-border-[1px] tw-border-[#e3eef2] tw-rounded-md tw-shadow-md tw-hover:bg-gray-50 tw-min-w-0 vincular-documento-table-row-fade-in ${isNewRow ? 'vincular-documento-table-row-fade-in--active' : ''}`}>
                            <div className="tw-w-full tw-flex tw-py-1 tw-items-center tw-min-w-0">
                              {/* Checkbox */}
                              <div className="tw-w-[40px] tw-flex tw-justify-center tw-items-center tw-flex-shrink-0">
                                <Checkbox
                                  checked={selectedRows.includes(rowId)}
                                  onChange={() => handleCheckboxChange(index, item)}
                                  disabled={isMultiple && selectedRows.length > 0 && !selectedRows.includes(rowId)}
                                />
                              </div>
                              <div className="tw-w-full tw-flex tw-items-center tw-min-w-0" style={{ paddingLeft: '10px' }}>
                              {/* Tipo Documento */}
                              <div className="tw-px-2 tw-flex-shrink-0 tw-min-w-0 tw-flex tw-items-center" style={{ width: '170px' }}>
                                <div className="text-truncate tw-text-xs lg:tw-text-sm tw-overflow-hidden">
                                  {item.meta?.summary || item.meta.typeLabel || item.meta.creditType || '-'}
                                </div>
                              </div>
                              {isCredit ? (
                                <div className="tw-flex tw-items-center tw-flex-grow tw-min-w-0">
                                  {/* Banco */}
                                  <div className="tw-px-2 tw-flex tw-items-center tw-overflow-hidden" style={{ flex: '0 0 30%', minWidth: 0 }}>
                                    <div className="text-truncate tw-text-xs lg:tw-text-sm">
                                      {banksFormat[item.name] || '-'}
                                    </div>
                                  </div>
                                  {/* Nº Cuota */}
                                  <div className="tw-px-2 tw-flex tw-items-center tw-overflow-hidden" style={{ flex: '0 0 15%', minWidth: 0 }}>
                                    <div className="tw-text-xs lg:tw-text-sm">
                                      {item.meta?.paymentNumber || '-'}
                                    </div>
                                  </div>
                                  {/* Identificador */}
                                  <div className="tw-px-2 tw-flex tw-items-center tw-overflow-hidden" style={{ flex: '1 1 55%', minWidth: 0 }}>
                                    <div className="text-truncate tw-text-blue-600 tw-text-xs lg:tw-text-sm">
                                      {item.meta?.identificator || '-'}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="tw-flex tw-items-center tw-flex-grow tw-min-w-0">
                                  {/* Rut - Hidden when invoice filter is active */}
                                  {selectedValues !== 'invoice' && (
                                    <div className="tw-px-2 tw-flex tw-items-center tw-overflow-hidden" style={{ flex: '0 0 20%', minWidth: 0 }}>
                                      <div className="text-truncate tw-text-xs lg:tw-text-sm">
                                        {formatRut(
                                          item.meta.isReceived
                                            ? item.meta?.issuerId
                                            : item.meta?.receiverId
                                        ) || "-"}
                                      </div>
                                    </div>
                                  )}
                                  {/* Razón Social */}
                                  <div className="tw-px-2 tw-flex tw-items-center tw-overflow-hidden" style={{ flex: '1 1 65%', minWidth: 0 }}>
                                    <div className="text-truncate tw-text-xs lg:tw-text-sm tw-overflow-hidden">
                                      {(item.meta.isReceived ? item.meta?.issuerName : item.meta?.receiverName) || '-'}
                                    </div>
                                  </div>
                                  {/* Folio */}
                                  <div className="tw-px-2 tw-flex tw-items-center tw-overflow-hidden" style={{ flex: '0 0 15%', minWidth: 0 }}>
                                    <div className="text-truncate tw-text-blue-600 tw-text-xs lg:tw-text-sm">
                                      {item.meta?.documentId || item.meta?.number || item.meta?.identificator || '-'}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {/* Fecha Emisión */}
                              <div className="tw-px-2 tw-flex-shrink-0 tw-text-left tw-flex tw-items-center" style={{ width: '95px' }}>
                                <div className="text-uppercase text-truncate tw-text-xs lg:tw-text-sm">
                                  {moment(item.documentDate).format('DD/MM/YYYY') || moment(item.meta?.issuedAt).format('DD/MM/YYYY') || '-'}
                                </div>
                              </div>
                              {/* Moneda Origen - Only shown when invoice filter is active */}
                              {selectedValues === 'invoice' && (
                                <div className="tw-px-2 tw-flex-shrink-0 tw-text-left tw-flex tw-items-center" style={{ width: '120px' }}>
                                  <div className="text-uppercase text-truncate tw-text-xs lg:tw-text-sm">
                                    {item.meta?.exchange?.originalAmount 
                                      ? formatMoneyWithCurrency(item.meta.exchange.originalAmount, item.meta.exchange.originalCurrency || 'USD')
                                      : '-'}
                                  </div>
                                </div>
                              )}
                              {/* Monto */}
                              <div className="tw-px-2 tw-flex-shrink-0 tw-text-left tw-flex tw-items-center" style={{ width: '110px' }}>
                                <div className="text-uppercase text-truncate tw-text-xs lg:tw-text-sm">
                                  {selectedValues === 'invoice' && item.meta?.exchange?.localAmount
                                    ? formatMoneyWithCurrency(item.meta.exchange.localAmount, 'CLP')
                                    : formatMoneyWithCurrency(item.amount || 0, item.currencyType || 'CLP')}
                                </div>
                              </div>
                              {/* Monto por Conciliar */}
                              <div
                                className="tw-px-2 tw-flex-shrink-0 tw-group tw-flex tw-items-center"
                                title={dt('tooltipsRow.editAmount')}
                                style={{ width: '300px' }}
                              >
                                <div className="tw-flex tw-items-center tw-w-full tw-justify-between tw-gap-3">
                                  <div className="tw-flex tw-items-center tw-gap-2">
                                    {editingIndex === index ? (
                                      <div className="tw-relative tw-flex tw-items-center" style={{ width: '160px' }}>
                                        <input
                                          type="number"
                                          value={editedValues[item.id] !== undefined ? editedValues[item.id] : displayValue}
                                          className={`tw-border tw-rounded tw-px-2 tw-py-1 tw-text-xs lg:tw-text-sm tw-transition-colors tw-duration-150 tw-w-full ${
                                            inputErrors[item.id]
                                              ? "tw-border-red-500 tw-bg-white"
                                              : "tw-border-gray-300 tw-cursor-text tw-bg-white"
                                          }`}
                                          style={{ paddingRight: '50px' }}
                                          onClick={(e) => e.stopPropagation()}
                                          onFocus={(e) => e.target.select()}
                                          ref={(ref) => {
                                            if (ref) {
                                              newRemainingAmounts.current[item.id] = ref;
                                            }
                                          }}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            let numValue = value === '' ? '' : parseFloat(value) || 0;
                                            
                                            // Cap the value at maxAllowed if it exceeds it
                                            if (numValue !== '' && numValue > maxAllowed) {
                                              numValue = maxAllowed;
                                              // Update the input field to show the capped value
                                              if (newRemainingAmounts.current[item.id]) {
                                                newRemainingAmounts.current[item.id].value = maxAllowed.toString();
                                              }
                                              setInputErrors((prev) => ({
                                                ...prev,
                                                [item.id]: `El monto no puede ser mayor que ${formatMoneyWithCurrency(maxAllowed, rowAmountCurrency)}.`,
                                              }));
                                            } else if (numValue !== '' && numValue < 0) {
                                              setInputErrors((prev) => ({
                                                ...prev,
                                                [item.id]: `El monto no puede ser menor a 0.`,
                                              }));
                                            } else {
                                              setInputErrors((prev) => ({
                                                ...prev,
                                                [item.id]: null,
                                              }));
                                            }
                                            
                                            setEditedValues((prev) => ({
                                              ...prev,
                                              [item.id]: numValue,
                                            }));
                                            if (newRemainingAmounts.current[item.id] && numValue !== '' && numValue <= maxAllowed) {
                                              newRemainingAmounts.current[item.id].value = value;
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              handleEditEnd(index, item);
                                            } else if (e.key === 'Escape') {
                                              e.preventDefault();
                                              handleCancelEdit(index, item);
                                            }
                                          }}
                                          min="0"
                                          max={maxAllowed}
                                          autoFocus={true}
                                        />
                                        <div 
                                          className="tw-absolute tw-right-0 tw-flex tw-items-center tw-gap-1 tw-pr-2" 
                                          style={{ 
                                            pointerEvents: 'auto', 
                                            zIndex: 10,
                                            right: '4px',
                                            top: '50%',
                                            transform: 'translateY(-50%)'
                                          }}
                                        >
                                          <FeatherIcon
                                            icon="x"
                                            size={16}
                                            className='tw-cursor-pointer tw-text-red-600 hover:tw-text-red-700'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCancelEdit(index, item);
                                            }}
                                            title={dt('common.cancel')}
                                          />
                                          <FeatherIcon
                                            icon="check"
                                            size={16}
                                            className='tw-cursor-pointer tw-text-green-600 hover:tw-text-green-700'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditEnd(index, item);
                                            }}
                                            title={dt('common.apply')}
                                          />
                                        </div>
                                      </div>
                                    ) : (
                                      <input
                                        type="text"
                                        readOnly={true}
                                        value={formatMoneyWithCurrency(editedValues[item.id] !== undefined ? editedValues[item.id] : displayValue, 'CLP')}
                                        className="tw-border tw-rounded tw-px-2 tw-py-1 tw-text-xs lg:tw-text-sm tw-transition-colors tw-duration-150 tw-bg-white tw-border-gray-300 tw-cursor-pointer group-hover:tw-border-blue-400"
                                        style={{ width: '160px' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditStart(index, item);
                                        }}
                                        onFocus={(e) => {
                                          e.preventDefault();
                                        }}
                                        ref={(ref) => {
                                          if (ref) {
                                            newRemainingAmounts.current[item.id] = ref;
                                          }
                                        }}
                                      />
                                    )}
                                    <FeatherIcon
                                      icon="edit-2"
                                      size={14}
                                      className="tw-text-gray-400 group-hover:tw-text-blue-600 tw-transition-colors tw-duration-150 tw-flex-shrink-0"
                                      title={dt('tooltipsRow.editClick')}
                                    />
                                  </div>
                                  {editingIndex !== index && canMatchOrAbonar() && (
                                    <div className="tw-flex tw-items-center tw-justify-center" style={{ flex: 1 }}>
                                      <Button
                                        type="button"
                                        color="blue"
                                        size="mini"
                                        onClick={() => {
                                          if (matchInFlightRef.current) return;
                                          const editedValue = editedValues[item.id];
                                          const amountToUse = (editedValue !== undefined && editedValue !== null && editedValue !== '' && editedValue > 0) 
                                            ? editedValue 
                                            : displayValue;
                                          match(item, amountToUse);
                                        }}
                                        disabled={isMultipleSelection || !!loadingButton}
                                        loading={loadingButton === `match-${item.id}`}
                                        style={{ flexShrink: 0 }}
                                      >
                                        {dt('vincular.reconcile')}
                                        <i className="arrow up right icon" style={{ marginLeft: '0.5em', transform: 'rotate(45deg)' }}></i>
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              </div>
                            </div>
                          </div>)
                        }
                      )}
                    </div>
                  )}
          </div>
          )}
        </div>
      </div>
    </div>
    <ModuleTcStatementPickerModal
      open={tcPicker.open}
      participants={tcPicker.participants}
      onCancel={() => {
        pendingTcMatchRef.current = null
        tcPicker.cancel()
      }}
      onConfirm={handleModuleTcPickerConfirm}
    />
    </>
  );
};

export default TableContent;